import React, { useState } from 'react';
import { authAPI, suppressSessionExpiryBriefly } from '../services/apiService';
import { Lock, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

interface AuthProps {
  onBack?: () => void;
}

export const Auth: React.FC<AuthProps> = ({ onBack }) => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      console.log('Attempting login with:', email);
      const response = await authAPI.login(email, password);
      
      console.log('Login result:', response);

      // API returns: { user: {...}, session: { access_token: "...", user: {...} } }
      const token = response.session?.access_token || response.token;
      
      if (token) {
        // Store token in localStorage
        localStorage.setItem('token', token);
        console.log('Login successful! Reloading...');
        
        // Suppress 401 interceptor for 3s to avoid false "session expired" on fresh login
        suppressSessionExpiryBriefly();
        
        toast.success('تم تسجيل الدخول بنجاح! 🎉');
        
        // Reload page to trigger App.tsx session check
        setTimeout(() => window.location.href = (import.meta.env.BASE_URL || '/analysis/'), 500);
      } else {
        throw new Error('فشل تسجيل الدخول');
      }
    } catch (error) {
      console.error('Catch block error:', error);
      const errorMessage = error instanceof Error ? error.message : 'خطأ في تسجيل الدخول';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-base)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px 16px',
      position: 'relative',
      overflow: 'hidden',
    }} dir="rtl">
      {/* Ambient orbs */}
      <div className="orb orb-primary" style={{ width: 400, height: 400, top: -80, right: -80, position: 'absolute' }} />
      <div className="orb orb-accent"  style={{ width: 300, height: 300, bottom: -60, left: -60, position: 'absolute' }} />

      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        style={{
          maxWidth: 420,
          width: '100%',
          background: 'rgba(255,255,255,0.04)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 24,
          padding: 40,
          position: 'relative',
          zIndex: 1,
          boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
        }}
      >
        {onBack && (
          <button
            onClick={onBack}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              color: 'var(--text-secondary)', background: 'none', border: 'none',
              cursor: 'pointer', fontSize: 13, marginBottom: 20, transition: 'color 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)'}
            onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-secondary)'}
          >
            <ArrowRight style={{ width: 16, height: 16 }} />
            <span>العودة للصفحة الرئيسية</span>
          </button>
        )}

        {/* Icon + heading */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16, margin: '0 auto 16px',
            background: 'linear-gradient(135deg,#6366F1,#8B5CF6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(99,102,241,0.4)',
          }}>
            <Lock style={{ width: 24, height: 24, color: 'white' }} />
          </div>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
            تسجيل الدخول
          </h2>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 6 }}>
            محلل استبانات ورش العمل التدريبية
          </p>
        </div>

        <form style={{ display: 'flex', flexDirection: 'column', gap: 16 }} onSubmit={handleSubmit}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
              البريد الإلكتروني
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              className="input-field"
              placeholder="admin@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
              كلمة المرور
            </label>
            <input
              id="password"
              name="password"
              type="password"
              required
              className="input-field"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                padding: '10px 14px',
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.25)',
                borderRadius: 10,
              }}
            >
              <div style={{ fontSize: 13, color: '#FCA5A5' }}>{error}</div>
            </motion.div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-primary"
            style={{ marginTop: 8 }}
          >
            {loading ? 'جاري التحقق...' : 'تسجيل الدخول'}
          </button>
        </form>
      </motion.div>
    </div>
  );
}